/**
 * The gate station's four-way gate, as a pure function.
 *
 * `/poste` is a kiosk, not a page: which screen it shows is decided entirely
 * by how far along the provisioning chain the browser has got — paired,
 * signed in, PIN accepted, on duty. That decision runs in the layout route's
 * `beforeLoad`, i.e. on every single navigation inside `/poste`, which is why
 * it lives here as data rather than as branches inside the route: the one bug
 * worth guarding against with a test is the gate bouncing a working desk back
 * to its own index every time the gardien opens the log.
 */

export type StationStage = "pairing" | "access" | "changePin" | "desk";

/** Where each stage lives. `desk` is the index; the rest are its own screens. */
export const STATION_PATHS = {
  pairing: "/poste/appairage",
  access: "/poste/acces",
  changePin: "/poste/code",
  desk: "/poste",
} as const satisfies Record<StationStage, string>;

/** The literal union, so a computed redirect target still typechecks as a route. */
export type StationPath = (typeof STATION_PATHS)[StationStage];

/** The three screens that exist only to get the station as far as the desk. */
const AUTH_PATHS: readonly string[] = [
  STATION_PATHS.pairing,
  STATION_PATHS.access,
  STATION_PATHS.changePin,
];

export interface StationState {
  /** A device token is in this browser's storage. */
  hasDeviceToken: boolean;
  /** A usable gardien session answered the server. */
  hasSession: boolean;
  /** The server told us at sign-in that this PIN is still the issued one. */
  mustChangePin: boolean;
  /** That gardien holds an open shift. */
  hasOpenShift: boolean;
}

/**
 * Order matters and is not arbitrary: each step's question is only meaningful
 * once the one above it is settled. A session with no open shift falls back to
 * the picker rather than the desk — ending the service leaves the station
 * unattended, and the next gardien must put their own name on what they wave
 * through.
 */
export function resolveStationStage(state: StationState): StationStage {
  if (!state.hasDeviceToken) return "pairing";
  if (!state.hasSession) return "access";
  if (state.mustChangePin) return "changePin";
  if (!state.hasOpenShift) return "access";
  return "desk";
}

/** Trailing slashes are a router detail, never a different screen. */
function normalize(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function isStationAuthPath(pathname: string): boolean {
  return AUTH_PATHS.includes(normalize(pathname));
}

/**
 * The path to redirect to, or null to stay put.
 *
 * The `desk` stage is deliberately permissive: everything under `/poste` that
 * isn't one of the three auth screens is a desk page (the log, the walk-in
 * queue), and a fully-provisioned station must be free to move between them.
 * Every other stage is exclusive — there is one screen to be on, and being
 * anywhere else is what needs correcting.
 */
export function stationRedirect(
  stage: StationStage,
  pathname: string,
): StationPath | null {
  const target = STATION_PATHS[stage];
  if (stage === "desk") {
    return isStationAuthPath(pathname) ? target : null;
  }
  return normalize(pathname) === target ? null : target;
}
